import React, { FC, useMemo } from "react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query/react";
import { SerializedError } from "@reduxjs/toolkit";
import { useDidMount } from "rooks";
import { Stack } from "@mui/material";
import {
  DashboardWidgetNormalizedCost,
  GetUsersMeOrganisationsCurrentWidgetsNormalizedCostByWidgetIdApiResponse,
  WidgetNormalizedCostAggregation,
} from "@/services/cloudchipr.api";
import { DashboardNotFoundWidget } from "@/components/pages/dashboard/components/widgets/common/DashboardNotFoundWidget";
import { NoCommitmentsCard } from "@/components/pages/dashboard/components/widgets/commitment-utlization/components/NoCommitmentsCard";
import { UnauthorizedWidgetHeader } from "@/components/unauthorized-pages/common/components/UnauthorizedWidgetHeader";
import { NormalizedCostWidgetAggregations } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/components/NormalizedCostWidgetAggregations";
import { NormalizedCostWidgetTableView } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/components/table/NormalizedCostWidgetTableView";
import { NormalizedCostWidgetHighChart } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/components/NormalizedCostWidgetHighChart";
import { chartTypeFromRechartToHighCharts } from "@/components/utils/helpers/chartTypeFromRechartToHighCharts";
import { HighchartsChartType } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";

interface InternalDashboardNormalizedCostWidgetProps
  extends DashboardWidgetNormalizedCost {
  data?: GetUsersMeOrganisationsCurrentWidgetsNormalizedCostByWidgetIdApiResponse;
  isError: boolean;
  error?: FetchBaseQueryError | SerializedError;
  onWidgetMount?(): void;
}

export const InternalDashboardNormalizedCostWidget: FC<
  InternalDashboardNormalizedCostWidgetProps
> = ({
  data,
  error,
  isError,
  name,
  forecast_option,
  dates,
  aggregation,
  visualization,
  onWidgetMount,
}) => {
  const status = error && "status" in error ? error?.status : 0;

  const hasAggregation = useMemo(
    () =>
      Object.keys(aggregation ?? {}).some(
        (key) => !!aggregation?.[key as keyof WidgetNormalizedCostAggregation],
      ),
    [aggregation],
  );

  const chartType = visualization.chart_type;

  useDidMount(() => {
    onWidgetMount?.();
  });

  if (isError && status !== 404) {
    return;
  }

  return (
    <Stack flexGrow={1} overflow="auto" height="100%">
      <UnauthorizedWidgetHeader name={name} dates={dates} />

      {status === 404 && (
        <DashboardNotFoundWidget name="" label="organization" />
      )}

      {!data?.regular_cost.length && status !== 404 && <NoCommitmentsCard />}

      {!!data?.regular_cost.length && status !== 404 && (
        <Stack flex={1} p={2}>
          <NormalizedCostWidgetAggregations
            totalCost={data?.total_cost}
            totalCostTrend={data?.trend}
            aggregations={aggregation ?? {}}
            forecastOption={forecast_option ?? "month"}
            forecastedCosts={data?.forecasted_costs}
            averageNormalizedMonthlyCost={data?.average_normalized_monthly_cost}
          />

          {chartType === "table" && (
            <NormalizedCostWidgetTableView
              normalized={data?.normalized}
              regular={data?.regular_cost}
              visualization={visualization}
            />
          )}

          {chartType !== "table" && (
            <NormalizedCostWidgetHighChart
              chartType={
                chartTypeFromRechartToHighCharts(
                  visualization.chart_type ?? "line",
                ) as HighchartsChartType
              }
              scale={visualization?.scale}
              regular={data?.regular_cost}
              normalized={data?.normalized}
              hasAggregations={hasAggregation}
              showLegends={visualization?.show_legends}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
